import Axios, { type AxiosRequestConfig } from "axios";
import qs from "qs";

import type {
  IBidder,
  IBidderDetails,
  IBlockingImpact,
  IBlockReason,
  ICity,
  ICountry,
  IPaginatedResponse,
  IUnblockReason,
  IUserBid,
  IUserBlockHistoryApi,
  IUserDeposit,
  IUserProfile,
  Nullable,
  UserByEmail,
} from "@utils/types";

export async function citiesByCountry(countryId: number, config?: AxiosRequestConfig) {
  const response = await Axios.get<ICity[]>(`/api/countries/${countryId}/cities/`, config);
  return response.data;
}

export async function countries(config?: AxiosRequestConfig) {
  const response = await Axios.get<ICountry[]>("/api/countries/", config);
  return response.data;
}

export async function byEmail(email: string) {
  const response = await Axios.get<UserByEmail>(`/api/users/${email}/`);
  return response.data;
}

/* Returns active User Block Reasons */
export async function getBlockReasons(config?: AxiosRequestConfig) {
  const response = await Axios.get<IBlockReason[]>("/api/users/block-reasons/", config);
  return response.data;
}
/* Returns active bids and deposits of the user */
export async function getBlockImpact(id: number | string, config?: AxiosRequestConfig) {
  const response = await Axios.get<IBlockingImpact>(`/api/users/${id}/blocking-impact/`, config);
  return response.data;
}

export async function blockUser(
  email: string,
  reason: string | null,
  message: string,
  sendBlockUserNotification: boolean = false,
) {
  const data = {
    reason: reason || undefined,
    notes: message || undefined,
    send_notification: sendBlockUserNotification,
  };
  const response = await Axios.patch(`/api/users/blocks/${email}/`, data);
  return response.data;
}

export async function getUnblockReasons(config?: AxiosRequestConfig) {
  const response = await Axios.get<IUnblockReason[]>("/api/users/unblock-reasons/", config);
  return response.data;
}

export async function unblockUser(
  email: string,
  message?: string,
  reason?: string | null,
  sendUnblockUserNotification: boolean = false,
) {
  const data = reason
    ? { reason, notes: message || undefined, send_notification: sendUnblockUserNotification }
    : message
      ? { unblock_reason: message }
      : undefined;
  const response = await Axios.delete(`/api/users/blocks/${email}/`, { data });
  return response.data;
}

export async function blockHistory(id: string | number) {
  const response = await Axios.get<IUserBlockHistoryApi[]>(`/api/users/${id}/block-history/`);
  return response.data;
}

export async function getBidders(
  params: {
    page: number;
    search?: string;
    auctions?: string[];
    ordering?: string;
    has_bids_placed?: string[];
    has_made_deposits?: string[];
    is_active?: string[];
    phone_verified?: string[];
    has_profile_complete?: string[];
    last_access_after?: string;
    last_access_before?: string;
  },
  config?: AxiosRequestConfig,
) {
  const {
    page,
    search,
    auctions,
    ordering,
    has_bids_placed,
    has_made_deposits,
    is_active,
    phone_verified,
    has_profile_complete,
    last_access_after,
    last_access_before,
  } = params;
  const response = await Axios.get<IBidder>("/api/users/bidders/", {
    ...config,
    params: {
      page,
      search,
      auction: auctions,
      ordering,
      has_bids_placed,
      has_made_deposits,
      is_active,
      phone_verified,
      has_profile_complete,
      last_access_after,
      last_access_before,
    },
    paramsSerializer: (p) => {
      return qs.stringify(p, {
        arrayFormat: "repeat",
      });
    },
  });
  return response.data;
}

export async function getBidderById(
  id: number,
  config?: AxiosRequestConfig,
): Promise<Nullable<IBidderDetails>> {
  const response = await Axios.get<IBidderDetails>(`/api/users/${id}/bidder/analytics/`, config);
  return response.data;
}

interface IDepositResults {
  id: number;
  asset_family: string;
  amount: number;
  refunded: boolean;
  bidder_user: number;
  auction: number;
  auction_location: string;
  auction_start_date: string;
  auction_end_date: string;
}

interface IDeposit {
  count: number;
  next: string;
  previous: string;
  results: IDepositResults[];
}

export async function getBidderDeposits(id: number) {
  const response = await Axios.get<IDeposit>(`/api/users/${id}/bidder/deposits/`);
  return response.data.results;
}

export type ExportBidderFilters = {
  search?: string;
  ordering?: string;
  auctions?: string[];
  has_bids_placed?: string[];
  has_made_deposits?: string[];
  is_active?: string[];
  phone_verified?: string[];
  has_profile_complete?: string[];
  last_access_after?: string;
  last_access_before?: string;
  // old value from ManageBidders.tsx
  bidder_ids?: number[];
  // new value for UserDetailsPage.tsx
  user_ids?: number[];
};

const toBackendParams = ({ auctions, ...rest }: ExportBidderFilters) => ({
  ...rest,
  auction: auctions,
});

export async function exportBidderIds(filters: ExportBidderFilters) {
  const response = await Axios.get<File>("/api/bidders/ids/download/", {
    params: toBackendParams(filters),
    responseType: "blob",
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
  });
  return response.data;
}

export async function exportBidders(filters: ExportBidderFilters) {
  const response = await Axios.get<File>("/api/bidders/bidders/export/", {
    params: toBackendParams(filters),
    responseType: "blob",
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
  });
  return response.data;
}

export async function getUserProfile(
  id: number,
  config?: AxiosRequestConfig,
): Promise<IUserProfile> {
  const response = await Axios.get<IUserProfile>(`/api/users/${id}/profile/`, config);
  return response.data;
}

export async function getUserBids(
  id: number,
  page = 1,
  config?: AxiosRequestConfig,
): Promise<IPaginatedResponse<IUserBid>> {
  const response = await Axios.get<IPaginatedResponse<IUserBid>>(`/api/users/${id}/bids/`, {
    ...config,
    params: { page },
  });
  return response.data;
}

export async function getUserDeposits(
  id: number,
  page = 1,
  config?: AxiosRequestConfig,
): Promise<IPaginatedResponse<IUserDeposit>> {
  const response = await Axios.get<IPaginatedResponse<IUserDeposit>>(`/api/users/${id}/deposits/`, {
    ...config,
    params: { page },
  });
  return response.data;
}

export async function downloadUserInfo(id: number) {
  return exportBidders({ user_ids: [id] });
}
